import { Autocomplete, Grid, GridSize, TextField, Tooltip, Typography } from '@mui/material';
import { FormSettingsTypeEnum } from 'src/modules/member/models/formSettingsType.enum';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { useFormContext } from 'react-hook-form';
import ControlledCheckBox from './controlledCheckbox';
import ControlledDatePicker from './controlledDatePicker';
import ControlledMultiCheckBox from './controlledMultiCheckbox';
import ControlledSelectBox from './controlledSelectBox';
import ControlledTextField from './controlledTextField';
import ControlledCheckBoxRadio from './controlledCheckBoxRadio';

type DynamicFormFieldProps = {
  formItem: any;
  index: number;
  renderFullWidth?: boolean;
};

const DynamicFormField = (props: DynamicFormFieldProps) => {
  const {
    formItem: { formSetting, options, formKey, value }, // formValue not used
    index,
    renderFullWidth = false,
  } = props;

  const { watch, setValue } = useFormContext();

  const fieldName = `items.${index}.value`;
  const formSettingsType = formSetting.type;

  const keyValueArray: SelectableItem[] = [];
  if (options && options.length) {
    options.map((x) => {
      keyValueArray.push({
        value: x.value,
        label: x.name,
      } as SelectableItem);
      return x;
    });
  }

  let returnElement: JSX.Element = <></>;

  if (formSettingsType === FormSettingsTypeEnum.Dropdown) {
    returnElement = (
      <ControlledSelectBox
        name={fieldName}
        label={formSetting.heading}
        required={formSetting.required}
        items={keyValueArray}
        disabled={formSetting.locked}
      />
    );
  } else if (formSettingsType === FormSettingsTypeEnum.Checkbox) {
    if (!keyValueArray.length) {
      // const isChecked = value === '1';
      returnElement = (
        <ControlledCheckBox
          name={fieldName}
          value={value}
          label={formSetting.heading}
          required={formSetting.required}
          disabled={formSetting.locked}
        />
      );
    } else {
      const isChecked: any[] = [];
      if (!Array.isArray(value)) {
        const valueCopy = [...value.split(',')];
        keyValueArray.forEach((item) => {
          isChecked.push(valueCopy.some((y) => item.value.toString() === y.toString()) ? item.value : null);
        });
      }
      returnElement = (
        <ControlledMultiCheckBox
          name={fieldName}
          value={isChecked || value}
          label={formSetting.heading}
          items={keyValueArray}
          disabled={formSetting.locked} // Not used
          required={formSetting.required} // Not used
        />
      );
    }
  } else if (formSettingsType === FormSettingsTypeEnum.CheckboxRadio) {
    returnElement = (
      <ControlledCheckBoxRadio
        name={fieldName}
        value={value}
        label={formSetting.heading}
        disabled={formSetting.locked}
        required={formSetting.required}
        items={keyValueArray}
      />
    );
  } else if (formSettingsType === FormSettingsTypeEnum.Text || formSettingsType === FormSettingsTypeEnum.Hidden) {
    // || (formSetting.type === FormSettingsTypeEnum.Interested && formSetting.locked)
    returnElement = (
      <ControlledTextField
        name={fieldName}
        label={formSetting.heading}
        disabled={formSetting.locked}
        required={formSetting.required}
        {...(formSettingsType === FormSettingsTypeEnum.Hidden ? { type: 'hidden' } : null)}
        inputProps={{ maxLength: formSetting.extra }}
        {...(formSettingsType === FormSettingsTypeEnum.Hidden ? { type: 'hidden' } : null)}
      />
    );
  }
  //
  /// /
  else if (formSettingsType === FormSettingsTypeEnum.Label || formSettingsType === FormSettingsTypeEnum.Header) {
    // || (formSetting.type === FormSettingsTypeEnum.Interested && formSetting.locked)
    returnElement = <Typography>{formSetting.heading} </Typography>;
  } else if (formSettingsType === FormSettingsTypeEnum.Multiline) {
    returnElement = (
      <ControlledTextField name={fieldName} label={formSetting.heading} required={formSetting.required} multiline rows={formSetting.extra} />
    );
  } else if (formSettingsType === FormSettingsTypeEnum.PostNr) {
    returnElement = (
      <ControlledTextField name={fieldName} required={formSetting.required} label={formSetting.heading} disabled={formSetting.locked} type='number' />
    );
  } else if (formSettingsType === FormSettingsTypeEnum.Interested) {
    // && !formSetting.locked
    returnElement = <ControlledDatePicker name={fieldName} label={formSetting.heading} disabled={formSetting.locked} />;
  } else if (formSettingsType === FormSettingsTypeEnum.Country) {
    const fieldPath = `items.${index}.value`;
    const formValuePath = `items.${index}.formValue`;

    const uniqueKeyValueArray = Array.from(new Map(keyValueArray.map((item) => [String(item.value), item])).values());
    const currentVal = watch(formValuePath);

    const priorityCountries = ['Sverige', 'Norge', 'Danmark', 'Finland'];

    const sortedOptions = [...uniqueKeyValueArray].sort((a, b) => {
      const aPriority = priorityCountries.indexOf(a.value);
      const bPriority = priorityCountries.indexOf(b.value);

      if (aPriority !== -1 && bPriority !== -1) {
        return aPriority - bPriority;
      }

      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;

      return a.label.localeCompare(b.label, 'sv');
    });

    const selectedOption =
      currentVal && typeof currentVal === 'object' && 'value' in currentVal
        ? currentVal
        : sortedOptions.find((o) => String(o.value) === String(currentVal) || o.label === currentVal) || null;

    returnElement = (
      <Autocomplete
        options={sortedOptions}
        value={selectedOption}
        onChange={(_, val) => {
          setValue(fieldPath, val ? val.value : '');
          setValue(formValuePath, val ? val.value : '');
        }}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label) || ''}
        isOptionEqualToValue={(opt, val) => String(opt.value) === String((val as any)?.value)}
        disabled={formSetting.locked}
        renderInput={(params) => (
          <TextField {...params} label={formSetting.heading} size='small' sx={{ width: '100%' }} InputLabelProps={{ shrink: true }} />
        )}
      />
    );
  } else {
    returnElement = (
      <ControlledTextField name={fieldName} label={formSetting.heading} required={formSetting.required} disabled={formSetting.locked} />
    );
  }

  const getFieldSize = (fieldSize: number, size: 'sm' | 'md'): GridSize => {
    if (size === 'sm') {
      return fieldSize > 1 ? 12 : 6;
    }
    return (fieldSize * 3) as unknown as GridSize;
  };

  const WrapWithTooltip = ({ children }) => {
    // Visa tooltip på låsta fält med errorMessage
    if (formSetting.locked && formSetting.errorMessage) {
      return (
        <Tooltip title={formSetting.errorMessage}>
          <span>{children}</span>
        </Tooltip>
      );
    }
    return children;
  };

  return (
    <>
      {renderFullWidth && (
        <Grid item key={formKey} xs={12}>
          <WrapWithTooltip>{returnElement}</WrapWithTooltip>
        </Grid>
      )}
      {!renderFullWidth && (
        <Grid item key={formKey} xs={12} sm={getFieldSize(formSetting.fieldSize, 'sm')} md={getFieldSize(formSetting.fieldSize, 'md')}>
          <WrapWithTooltip>{returnElement}</WrapWithTooltip>
        </Grid>
      )}
    </>
  );
};

export default DynamicFormField;
